import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  useMediaQuery,
  CircularProgress
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";
import { Trash2, } from "lucide-react";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const CURRENT_STEP_INDEX = 3;

export const SERVICE_OPTIONS = [
  { key: "boarding", labelKey: "ServiceGrooming", unitKey: "UnitPerTime", icon: "/icons/groom.svg" },
  { key: "nailTrim", labelKey: "ServiceNailTrim", unitKey: "UnitPerTime", icon: "/icons/nailTrim.svg" },
  { key: "bathing", labelKey: "ServiceBathing", unitKey: "UnitPerTime", icon: "/icons/bath.svg" },
  { key: "shaving", labelKey: "ServiceMedication", unitKey: "UnitPerTime", icon: "/icons/medication.svg" },
  { key: "playPrice", labelKey: "ServicePlaytime", unitKey: "UnitPerMinute", icon: "/icons/play.svg" },
];

export const PICKUP = [
  { key: "pickup", labelKey: "ServicePickup", unitKey: "UnitPerTrip", icon: "/icons/pickup.svg" },
];

const HotelAdd_RoomInfoAdd_1 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const edit = searchParams.get('edit') || '';
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { saveStep, fetchDraft, hotelData, loading, } = useHotel();
  const [isSaving, setIsSaving] = useState(false);

  const [selectedServices, setSelectedServices] = useState({});
  const [servicePrices, setServicePrices] = useState({});
  const [playTime, setPlayTime] = useState(0);
  const [customServiceLabel, setCustomServiceLabel] = useState("");
  const [customServiceDescription, setCustomServiceDescription] = useState("");
  const [customServices, setCustomServices] = useState([]);
  const [errorModal, setErrorModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchDraft(id);
    }
  }, [id, fetchDraft]);

  useEffect(() => {
    if (hotelData.additionalServices) {
      const { selectedServices, servicePrices, customServices } = hotelData.additionalServices;
      setSelectedServices(selectedServices || {});
      setPlayTime(servicePrices?.playtime || 0);
      setServicePrices(servicePrices || {});
      if (customServices && customServices.length > 0) {
        setCustomServices(customServices);
      }
    }
  }, [hotelData]);

  const toggleService = (key) => {
    setSelectedServices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateServicePrice = (key, value) => {
    setServicePrices((prev) => ({ ...prev, [key]: value }));
  };

  const addCustomService = () => {
    setCustomServices((prev) => [...prev, { label: customServiceLabel, description: customServiceDescription }]);
  };

  const deleteCustomService = (index) => {
    setCustomServices((prev) => prev.filter((_, i) => i !== index));
  };

  const priceFieldSx = {
    width: isMobile ? "80px" : "100px",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px",
    },
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);

      // Build additional services payload based on selectedServices
      const additionalServices = {};
      if (Object.values(selectedServices).some(Boolean)) {
        additionalServices.selectedServices = selectedServices;
      }
      if (Object.values(servicePrices).some(Boolean)) {
        if (servicePrices.playtime < 0) {
          setErrorModal(true);
          return;
        }
        if (servicePrices.playPrice < 0) {
          setErrorModal(true);
          return;
        }
        if (servicePrices.pickup < 0) {
          setErrorModal(true);
          return;
        }
        if (Object.values(servicePrices).some(price => price < 0)) {
          setErrorModal(true);
          return;
        }
        additionalServices.servicePrices = servicePrices;
      }
      if (playTime) {
        additionalServices.servicePrices.playtime = Number(playTime);
      }
      if (customServices.length > 0) {
        additionalServices.customServices = customServices;
      }

      // Save additional services step
      await saveStep("additionalServices", additionalServices);

      // Navigate to next step
      navigate(edit ? `/hotel/${id}/information` : "/hotel/add/room-info-add-2/" + id);
    } catch (err) {
      console.error("Error saving additional services:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      setIsSaving(true);

      // Build additional services payload based on selectedServices
      const additionalServices = {};
      if (Object.values(selectedServices).some(Boolean)) {
        additionalServices.selectedServices = selectedServices;
      }
      if (Object.values(servicePrices).some(Boolean)) {
        additionalServices.servicePrices = servicePrices;
      }
      if (playTime) {
        additionalServices.servicePrices.playtime = Number(playTime);
      }
      if (customServices.length > 0) {
        additionalServices.customServices = customServices;
      }

      // Save additional services step
      await saveStep("additionalServices", additionalServices);

      // Navigate to next step
      navigate("/hotel/onboarding/");
    } catch (err) {
      console.error("Error saving additional services:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const customFieldSx = {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
    },
  };

  const checkboxSx = {
    color: "#D1D1D1",
    "&.Mui-checked": { color: "#31BACD" },
  };

  const isNextDisabled =
    (!Object.values(selectedServices).some(Boolean) &&
      (customServices.length === 0 || customServices[0]?.label.trim() === "")
    ) ||
    SERVICE_OPTIONS.some(
      (service) => selectedServices[service.key] && !servicePrices[service.key]
    ) ||
    (selectedServices["playPrice"] && !playTime) ||
    (selectedServices["pickup"] && !servicePrices["pickup"]);

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto" }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index <= CURRENT_STEP_INDEX ? "#31BACD" : "transparent",
                    border: index <= CURRENT_STEP_INDEX ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index <= CURRENT_STEP_INDEX ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === CURRENT_STEP_INDEX ? "600" : "400",
                    color: index === CURRENT_STEP_INDEX ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000", mb: 4 }}>
            {t('RoomInfoAddStep1Title')}
          </Typography>

          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
              <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                {t('AdditionalServicesQuestion')}
              </Typography>

              {SERVICE_OPTIONS.map((service) => (
                <Box key={service.key} sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5 }}>
                  <Checkbox
                    checked={!!selectedServices[service.key]}
                    onChange={() => toggleService(service.key)}
                    sx={checkboxSx}
                  />
                  <Box
                    component="img"
                    src={service.icon}
                    alt=""
                    sx={{ width: 30, height: 30, flexShrink: 0 }}
                  />
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", }}>
                    {t(service.labelKey)}
                  </Typography>
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", flexShrink: 0 }}>
                    HK$
                  </Typography>
                  <TextField
                    type="number"
                    inputProps={{ min: 0 }}
                    value={servicePrices[service.key] || ""}
                    onChange={(e) => updateServicePrice(service.key, e.target.value)}
                    sx={priceFieldSx}
                  />
                  {service.unitKey === "UnitPerMinute" && (
                    <>
                      <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", flexShrink: 0 }}>
                        /
                      </Typography>
                      <TextField
                        type="number"
                        inputProps={{ min: 0 }}
                        value={playTime || ""}
                        onChange={(e) => setPlayTime(e.target.value)}
                        sx={priceFieldSx}
                      />
                    </>
                  )}
                  <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#000", flexShrink: 0 }}>
                    {t(service.unitKey)}
                  </Typography>
                </Box>
              ))}

              {PICKUP.map((service) => (
                <Box key={service.key}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5 }}>
                    <Checkbox
                      checked={!!selectedServices[service.key]}
                      onChange={() => toggleService(service.key)}
                      sx={checkboxSx}
                    />
                    <Box
                      component="img"
                      src={service.icon}
                      alt=""
                      sx={{ width: 30, height: 30, flexShrink: 0 }}
                    />
                    <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", }}>
                      {t(service.labelKey)}
                    </Typography>

                    {selectedServices[service.key] && (
                      <Box sx={{ width: "80%", }}>
                        <TextField
                          fullWidth
                          placeholder={t('PleaseEnterFeeSchedule')}
                          value={servicePrices[service.key] || ""}
                          onChange={(e) => updateServicePrice(service.key, e.target.value)}
                          sx={customFieldSx}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}


              {/* Custom Services */}
              {customServices.map((row, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5 }}>
                  <Checkbox
                    checked={true}
                    sx={checkboxSx}
                  />
                  <Box
                    component="img"
                    src={"/icons/additional-services.svg"}
                    alt=""
                    sx={{ width: 30, height: 30, flexShrink: 0 }}
                  />
                  <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", }}>
                    {t(row.label)}
                  </Typography>
                  <Box sx={{ width: "70%", }}>
                    <TextField
                      fullWidth
                      value={row.description || ""}
                      sx={customFieldSx}
                    />
                  </Box>
                  <Button
                    onClick={() => {
                      const fillIndex = customServices.findIndex(
                        (p) => p.label === row.label
                      );
                      deleteCustomService(fillIndex);
                    }}
                    sx={{
                      color: "#31BACD",
                      textTransform: "none",
                      fontSize: isMobile ? "14px" : "16px",
                      minWidth: "auto",
                    }}
                  >
                    <Trash2 size={14} />
                  </Button>
                </Box>
              ))}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5 }}>
                <Box
                  component="img"
                  src={"/icons/additional-services.svg"}
                  alt=""
                  sx={{ width: 30, height: 30, flexShrink: 0 }}
                />
                <TextField
                  fullWidth
                  placeholder={t('CustomServiceLabelPlaceholder')}
                  value={customServiceLabel}
                  onChange={(e) => setCustomServiceLabel(e.target.value)}
                  sx={customFieldSx}
                />
                <TextField
                  fullWidth
                  placeholder={t('CustomServiceDescriptionPlaceholder')}
                  value={customServiceDescription}
                  onChange={(e) => setCustomServiceDescription(e.target.value)}
                  sx={customFieldSx}
                />
                <Button
                  onClick={addCustomService}
                  sx={{
                    color: "#31BACD",
                    textTransform: "none",
                    fontSize: isMobile ? "14px" : "16px",
                    minWidth: "auto",
                    flexShrink: 0,
                  }}
                >
                  {t('AddService')}
                </Button>
              </Box>
            </Box>
          )}

          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={isNextDisabled || isSaving}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={isNextDisabled || isSaving}
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>

      {(errorModal) && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 2 : 4,
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "24px",
              width: isMobile ? "80%" : "auto",
              maxWidth: "700px",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Box sx={{ width: isMobile ? "100%" : "320px", flexShrink: 0, }}>
              <Box
                component="img"
                src="/icons/ppclogo.png"
                alt="Pet Pet Club"
                sx={{ height: isMobile ? "20px" : "24px", mb: 2 }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, }}>
                <img src="/icons/redCross.svg" alt="lock" style={{ width: "32px", height: "auto" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "700", color: "#000" }}>
                  {t("NumberCannotBeNegative")}
                </Typography>
                <Typography sx={{ fontSize: "16px", color: "#000" }}>
                  {t("PleaseEnterValidNumber")}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, }}>
                  <Button
                    onClick={() => setErrorModal(false)}
                    sx={{
                      padding: "8px 12px",
                      backgroundColor: "#31BACD",
                      color: "#fff",
                      textTransform: "none",
                      fontSize: "16px",
                      fontWeight: "600",
                      borderRadius: "10px",
                      "&:hover": { backgroundColor: "#89c4cb" },
                    }}
                  >
                    {t("Return")}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Footer />
    </div>
  );
};

export default HotelAdd_RoomInfoAdd_1;
